import { EContentType, EMaturityRating, EProjectStatus } from 'types/src/types';
import { optional, z } from 'zod';
import { compareVersions, VERSION_RE } from '../utils/projectVersion';
import { vi } from 'zod/v4/locales';
import { POST_MEDIA_LIMITS } from '../constants/constants';
import { FeatureType } from './social/components/TokenFeatureSelector';

export const bitFormSchema = z.object({
  title: z.string().nonempty('Title is required'),
  tags: z
    .array(z.string())
    .max(3, 'You can add up to 3 tags')
    .min(1, 'At least one tag is required'),
  fields: z
    .array(
      z.union([
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('paragraph'),
          order: z.number(),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('h1'),
          order: z.number(),
          level: z.literal(1),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('h2'),
          order: z.number(),
          level: z.literal(2),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('numbered-list'),
          order: z.number(),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('bulleted-list'),
          order: z.number(),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('quote'),
          order: z.number(),
          content: z.string().nonempty('Field could not be empty'),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('divider'),
          order: z.number(),
          content: z.string().optional(),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('image'),
          order: z.number(),
          content: z.string().optional(),
          image: z.object({
            file: z.any().optional(),
            url: z.string().url(),
          }),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('multi-images'),
          order: z.number(),
          content: z.string().optional(),
          images: z.array(
            z.object({
              file: z.any().optional(),
              url: z.string().url(),
            }),
          ),
          serverId: z.number().readonly().optional(),
        }),
        z.object({
          id: z.number().readonly().optional(),
          clientId: z.string(),
          type: z.literal('embed-video'),
          order: z.number(),
          content: z.string().url(),
          serverId: z.number().readonly().optional(),
        }),
      ]),
    )
    .min(1, 'At least one field is required')
    .refine(
      (fields: any[]) =>
        fields.some((field) =>
          [
            'paragraph',
            'h1',
            'h2',
            'numbered-list',
            'bulleted-list',
            'quote',
            'embed-video',
          ].includes(field.type),
        ),
      {
        message: 'At least one text-based field is required',
        path: ['fields'],
      },
    )
    .refine(
      (fields: any[]) => {
        const totalLength = fields
          .filter((field) =>
            [
              'paragraph',
              'h1',
              'h2',
              'numbered-list',
              'bulleted-list',
              'quote',
              'embed-video',
            ].includes(field.type),
          )
          .reduce((sum, field) => sum + (field.content?.length || 0), 0);
        return totalLength >= 200;
      },
      {
        message: 'Total text length must be at least 200 characters',
        path: ['fields'],
      },
    ),
  coverImage: z.instanceof(File).optional(),
  coverImageUrl: z.string(),
  visibility: z.string(),
});

export const projectFormSchema = z
  .object({
    title: z
      .string()
      .max(40, 'Title must be at most 40 characters')
      .nonempty('Title is required'),
    tags: z
      .array(z.string())
      .max(3, 'You can add up to 3 tags')
      .min(1, 'At least one tag is required'),
    shortDescription: z
      .string()
      .max(55, 'Short description must be at most 55 characters'),
    description: z
      .string()
      .min(200, 'Description must be at least 200 characters'),
    coverImage: z.instanceof(File).optional(),
    coverImageUrl: z.string(),
    status: z.nativeEnum(EProjectStatus),
    price: z
      .string()
      .trim()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          return /^[0-9]+([.,][0-9]{1,2})?$/.test(val);
        },
        {
          message: 'Use a valid price like 9.99',
        },
      ),
    systems: z.array(z.string()).optional().default([]),
    maturityRating: z.nativeEnum(EMaturityRating),
    contentType: z.nativeEnum(EContentType),
    trailers: z
      .array(
        z.object({
          clientId: z.string(),
          url: z.string().url('Invalid URL format'),
          order: z.number(),
          title: z.string(),
          thumbnail: z.string().optional(),
        }),
      )
      .optional(),
    projectScreenshots: z
      .array(
        z.object({
          clientId: z.string(),
          file: z.any().optional(),
          url: z.string().url(),
          order: z.number(),
        }),
      )
      .optional(),
    iosLink: z
      .union([z.string().trim().url('Invalid iOS URL'), z.literal('')])
      .optional(),
    androidLink: z
      .union([z.string().trim().url('Invalid Android URL'), z.literal('')])
      .optional(),
    isFree: z.boolean(),
    version: z
      .string()
      .nonempty('Version is required')
      .regex(
        VERSION_RE,
        'Use X or X.Y or X.Y.Z; each segment 1–99 (e.g., 1, 1.1, 1.12, 1.1.2)',
      ),
  })
  .superRefine((data: any, ctx: any) => {
    if (data.status === EProjectStatus.RELEASED) {
      if (!Array.isArray(data.systems) || data.systems.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['systems'],
          message: 'Select at least one system',
        });
      }
      if (!data.isFree) {
        if (!data.price || data.price.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['price'],
            message: 'Price is required for paid projects',
          });
          return;
        }
        const normalized = data.price.replace(',', '.');
        const numeric = Number(normalized);

        if (Number.isNaN(numeric)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['price'],
            message: 'Enter a valid number',
          });
          return;
        }

        if (numeric < 0.99) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: 0.99,
            inclusive: true,
            type: 'number',
            origin: 'number',
            path: ['price'],
            message: 'Price must be at least $0.99',
          });
        }

        if (numeric > 50) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: 50,
            inclusive: true,
            type: 'number',
            origin: 'number',
            path: ['price'],
            message: 'Price must be at most $50',
          });
        }
      }
    }

    const hasIOS =
      data.systems?.includes('IOS') || data.systems?.includes('iOS');
    const hasAndroid = data.systems?.includes('Android');

    if (hasIOS && !data.iosLink) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['iosLink'],
        message: 'iOS link is required when IOS is selected',
      });
    }
    if (hasAndroid && !data.androidLink) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['androidLink'],
        message: 'Android link is required when Android is selected',
      });
    }
    if (data.iosLink) {
      const appStore = /^https?:\/\/apps\.apple\.com\/.+/i.test(data.iosLink);
      if (!appStore) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iosLink'],
          message: 'Use a valid App Store URL (apps.apple.com)',
        });
      }
    }
    if (data.androidLink) {
      const playStore = /^https?:\/\/play\.google\.com\/store\/apps\//i.test(
        data.androidLink,
      );

      if (!playStore) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['androidLink'],
          message: 'Use a valid Google Play URL',
        });
      }
    }

    const minVersion = (ctx as any)?.context?.minVersion as string | undefined;
    if (minVersion && VERSION_RE.test(minVersion)) {
      if (compareVersions(data.version, minVersion) < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['version'],
          message: `Version must be \u2265 ${minVersion}`,
        });
      }
    }
  });

const igdbGameMetaSchema = z.object({
  igdbId: z.number(),
  name: z.string(),
  summary: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
  coverImage: z.string().nullable().optional(),
  thumb: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  developer: z.string().nullable().optional(),
  publisher: z.string().nullable().optional(),
  screenshots: z.array(z.string()).optional(),
  videos: z.array(z.object({ source: z.string(), url: z.string() })).optional(),
});

const imageItemSchema = z.object({
  file: z.any().optional(),
  url: z.string().url(),
});

export const makePostFormSchema = (active: FeatureType[]) =>
  z
    .object({
      text: z.string().trim().min(3, 'Text too short'),
      textPlain: z
        .string()
        .trim()
        .min(3, 'Text too short')
        .max(400, 'Text must be at most 400 characters'),

      gifId: z.string().optional(),
      uploadedImagesUrl: z
        .array(imageItemSchema)
        .max(
          POST_MEDIA_LIMITS.MAX_IMAGES,
          `Maximum ${POST_MEDIA_LIMITS.MAX_IMAGES} images`,
        )
        .optional(),
      video: z
        .object({
          file: z.any().optional(),
          url: z.string().optional(),
        })
        .optional(),
      youTubeVideoUrl: z.string().optional().or(z.literal('')),
      igdbGameId: z.any().nullable(),
      igdbGameMeta: igdbGameMetaSchema.nullable(),
      projectId: z.number().nullable(),
    })
    .superRefine((val, ctx) => {
      const plainText = String(val.textPlain ?? '').trim();

      const hasImages = (val.uploadedImagesUrl?.length ?? 0) > 0;
      const hasVideo = Boolean(val.video?.file) || Boolean(val.video?.url);
      const hasYoutube = Boolean((val.youTubeVideoUrl ?? '').trim().length > 0);
      const hasGif = Boolean((val.gifId ?? '').trim().length > 0);

      if (active.includes('image') && !hasImages) {
        ctx.addIssue({
          code: 'custom',
          path: ['uploadedImagesUrl'],
          message:
            'Images feature is enabled. Add at least one image or disable the feature.',
        });
      }

      if (active.includes('video') && !hasVideo) {
        ctx.addIssue({
          code: 'custom',
          path: ['video'],
          message:
            'Video feature is enabled. Upload a video or disable the feature.',
        });
      }

      if (active.includes('youtube')) {
        const url = (val.youTubeVideoUrl ?? '').trim();
        if (!url) {
          ctx.addIssue({
            code: 'custom',
            path: ['youTubeVideoUrl'],
            message:
              'YouTube feature is enabled. Add a YouTube link or disable the feature.',
          });
        } else {
          try {
            new URL(url);
          } catch {
            ctx.addIssue({
              code: 'custom',
              path: ['youTubeVideoUrl'],
              message: 'Invalid YouTube URL',
            });
          }
        }
      }

      if (active.includes('gif') && !hasGif) {
        ctx.addIssue({
          code: 'custom',
          path: ['gifId'],
          message: 'GIF feature is enabled. Pick a GIF or disable the feature.',
        });
      }
    });

export type PostFormSchema = ReturnType<typeof makePostFormSchema>;
export type PostFormData = z.infer<PostFormSchema>;
